import { freigabeVormerken } from './sprungmarke.ts';
import { useEffect, useRef, useState } from 'react';
import { api, datumKurz, type Anmelder, tagIso} from '../api.ts';
import { anlassText } from './anlasstext.ts';
import { Dateifenster } from './Dateifenster.tsx';

// Die Glocke: was den Innendienst angeht, aus vier Ecken.
//
// ORIGINALTEXT MARVIN (25.08.2026)
//   „sollen wir eine notification glocke oben noch einbinden mit den infos
//    und die man dann als gelesen markieren muss?"
//   „wir braucehn ja nicht nur die infos der automatik."
//   „außerdem die textliche aufteilung darin ist schlecht"
//
// ORIGINALTEXT MARVIN (27.08.2026)
//   „kannst du die benachrichtigungen attraktiver machen. das sit so monton
//    zum lesen und anklicken. außerdem ist die frage: verschwinden die
//    einzelnen meldungen automatisch wenn sie behoben wurden?"
//
// SEIN BILDSCHIRMFOTO ZEIGTE 55 MELDUNGEN, und die ZAHL war das eigentliche
// Problem. Jeder Lauf der Automatik schrieb täglich eine neue Zeile, und keine
// ging je wieder weg: „Fertigstellungsanzeige fällig (13)" stand fünfmal
// untereinander, viermal davon mit dem Stand von vorgestern. Eine Liste, in der
// vier von fünf Zeilen nichts mehr sagen, liest niemand - und beim sechsten Mal
// auch die fünfte nicht mehr.
//
// DIE HÄLFTE DER ARBEIT STECKT DESHALB IN MIGRATION 052 und nicht hier: eine
// Meldung der Automatik ist ein BEFUND und gilt bis zum nächsten Lauf. Was
// abgelöst ist, steht in der Ecke „Erledigt" und nicht mehr in der Liste.
//
// DIE ANDERE HÄLFTE IST DIESE DATEI, und sie ändert vier Dinge:
//
//   1. NACH TAGEN GEGLIEDERT. „Heute", „Gestern", dann das Datum. Fünfzig
//      gleich aussehende Zeitstempel untereinander sind keine Ordnung.
//   2. JEDE ECKE HAT EIN ZEICHEN UND EINE FARBE. Man sieht beim Überfliegen,
//      ob eine Zeile eine Aufgabe (rot, Post), eine Entscheidung (orange,
//      Eingang), ein Mensch (blau, Partner) oder ein Bericht ist.
//   3. GELESENES ZIEHT UM. Es verschwindet nicht - man soll es wiederfinden -,
//      aber es steht nicht mehr in der Liste.
//   4. DER HAKEN IST EIN HAKEN. Vorher stand an jeder Zeile ein Knopf mit der
//      Aufschrift „Gelesen", fünfzigmal derselbe. Jetzt ein rundes Feld am
//      rechten Rand, und die Aufschrift steht im Titel.
//
// ZU PUNKT 3, NACHGETRAGEN AM 27.08.2026.
//
// ORIGINALTEXT MARVIN
//   „wenn einträge auf der notification als gelsen markiert wurden, dann soll
//    der eintrag verschwinden."
//
// Er hat recht, und es war ein halber Schritt: eine abgehakte Zeile schrumpfte
// auf ihre Überschrift und BLIEB STEHEN. Fünfzig geschrumpfte Zeilen nehmen
// genauso viel Platz weg wie fünfzig ganze, und dann sieht Arbeit aus wie
// keine Arbeit. Jetzt geht sie aus der Liste und in die Ecke „Gelesen"; ein
// zweiter Klick auf den Haken holt sie zurück.
//
// DER TELEGRAM-VERSAND BLEIBT. Er ist der Weg aufs Telefon, und morgens um
// sechs, wenn der Bagger anrollt, ist er der richtige.

/**
 * Die Ecken, in der Reihenfolge, in der sie im Filter stehen.
 *
 * „Gelesen" und „Erledigt" sind KEINE Ecken, sondern zwei Schnitte quer durch
 * alle vier. Sie stehen trotzdem hier, weil sie dieselbe Frage beantworten:
 * was steht noch da, und was nicht mehr. Und sie stehen GANZ RECHTS - es sind
 * die beiden Ecken, in denen nichts zu tun ist.
 *
 * SIE SIND NICHT DASSELBE. „Gelesen" hat ein Mensch abgehakt; „Erledigt" hat
 * sich von selbst gegeben. Wer beides zusammenwürfe, könnte hinterher nicht
 * mehr unterscheiden, ob jemand gearbeitet hat.
 */
const ECKEN: { wert: string; name: string; kurz: string; farbe: string }[] = [
  { wert: '',          name: 'Alles',            kurz: 'Alles',     farbe: '' },
  { wert: 'versand',   name: 'Nicht zugestellt', kurz: 'Post',      farbe: 'rot' },
  /* WAS AUF FREIGABE WARTET (Migration 126). Sie steht weit vorn und trägt
     Rot: eine Behördensendung, die wartet, hält einen Antrag auf, und an dem
     hängt eine Frist. */
  { wert: 'freigabe',  name: 'Wartet auf Freigabe', kurz: 'Freigabe', farbe: 'rot' },
  /* BEHÖRDE (.528): FAZ zum Versand bereit, FAZ überfällig, Fristen, Anordnungen,
     Behördenpost offen. Marvin, 28.10.2026: „Trotzdem sehe ich die gar nicht hier." */
  { wert: 'behoerde',  name: 'Behörde: FAZ, Fristen, Anordnungen', kurz: 'Behörde', farbe: 'rot' },
  /* RECHNUNGEN ZUR PRÜFUNG UND NEUE GUTSCHRIFTEN (.256). Nur für den, der
     freigeben darf und die Zahlen sieht; der Server liefert die Ecken. */
  { wert: 'rechnung',  name: 'Rechnungen zur Prüfung', kurz: 'Rechnung', farbe: 'orange' },
  { wert: 'gutschrift', name: 'Neue Gutschriften', kurz: 'Gutschrift', farbe: 'blau' },
  { wert: 'gebuehr',   name: 'Gebühren zu bezahlen', kurz: 'Gebühr', farbe: 'orange' },
  /* ÜBERWEISUNGEN, DIE IN REVOLUT AUF DIE FREIGABE WARTEN (.263). */
  { wert: 'zahlentwurf', name: 'In Revolut freigeben', kurz: 'Revolut', farbe: 'orange' },
  { wert: 'wiedervorlage', name: 'Wiedervorlage', kurz: 'Vorlage',  farbe: 'orange' },
  { wert: 'eingang',   name: 'Posteingang',      kurz: 'Eingang',   farbe: 'orange' },
  { wert: 'partner',   name: 'Partnerfirmen',    kurz: 'Partner',   farbe: 'blau' },
  { wert: 'mir',       name: 'Für mich',         kurz: 'Für mich',  farbe: 'blau' },
  { wert: 'automatik', name: 'Automatik',        kurz: 'Automatik', farbe: 'grau' },
  /* NUR ZUR INFO (.467): was sauber lief, zählt nicht an der Glocke und steht nicht unter „Alles". */
  { wert: 'info',      name: 'Nur zur Info (lief sauber)', kurz: 'Info', farbe: 'grau' },
  { wert: 'gelesen',   name: 'Abgehakt',         kurz: 'Gelesen',   farbe: 'grau' },
  { wert: 'erledigt',  name: 'Erledigt',         kurz: 'Erledigt',  farbe: 'gruen' },
];

/**
 * WELCHE ECKEN DIESER ANMELDER BEKOMMT, SAGT DER SERVER.
 *
 * Er liefert sie in `ecken` mit; hier wird nur gefiltert. Eine zweite Liste
 * „welche Ecke gehört wem" neben der im Server liefe beim ersten Nachtrag
 * auseinander - und dann stünde hier ein Filterknopf, der immer null trifft.
 * Ein Filter, der immer null trifft, ist kein Filter, sondern ein Knopf, der
 * nichts tut.
 *
 * „Alles", „Gelesen" und „Erledigt" sind keine Ecken, sondern Schnitte quer
 * durch alle; sie stehen immer da, sobald es überhaupt etwas gibt.
 */
/** Eine Meldung, hinter der eine Behördenfreigabe wartet. */
function istFreigabe(m: any): boolean {
  return m?.art === 'freigabe' || m?.anlass === 'behoerdenfreigabe';
}

const QUER = new Set(['', 'gelesen', 'erledigt']);

/** In diesen beiden Ecken ist nichts abzuhaken. */
const RUHIG = new Set(['gelesen', 'erledigt']);

/* DIE ANLÄSSE KOMMEN VOM SERVER (`anlaesse` in der Antwort).
 *
 *   ORIGINALTEXT MARVIN (08.09.2026)
 *     „«Termin» wird groß geschrieben am anfang"
 *
 * Hier stand eine Liste mit siebzehn Einträgen, und `termin` war keiner
 * davon - also stand dort der Rohwert, klein geschrieben. Der Kommentar
 * daneben nannte das „einen harmlosen Rückfall"; harmlos war er nicht, denn
 * die Liste war ZUR HÄLFTE FALSCH: sechs ihrer Einträge gehörten zu Läufen,
 * die es nicht mehr gibt, und sechs Läufe, die es gibt, fehlten. **Eine
 * halbierte Liste sieht aus wie eine ganze.**
 *
 * Ihre Namen stehen in `automatik.name`, in der Datenbank, und werden dort
 * gepflegt; alles Übrige leitet `regeln/anlasstext.ts` ab. Was durch beides
 * fällt, ist freier Text (`AAZ an Koblenz`) und wird lesbar gemacht, nicht
 * benannt - siehe `anlasstext.ts` daneben.
 */

/**
 * Ein Zeichen je Ecke.
 *
 * GEZEICHNET STATT GELADEN, aus demselben Grund wie die Glocke selbst: ein
 * Symbolzeichen aus einer Schrift sieht auf jedem zweiten Gerät anders aus,
 * und auf manchen ist es ein leeres Kästchen.
 */
function Eckenzeichen({ art }: { art: string }) {
  const g = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.7,
              strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      {art === 'info' && (<>
        {/* Ein i im Kreis: lief sauber, nur zur Kenntnis (.467). */}
        <circle cx="12" cy="12" r="8.5" {...g} />
        <path d="M12 11v5.5M12 7.8v.1" {...g} />
      </>)}
      {art === 'versand' && (<>
        {/* Ein Umschlag mit einem Ausrufezeichen: Post, die nicht ankam. */}
        <path d="M3 6.5h13v11H3z" {...g} />
        <path d="M3 7l6.5 5L16 7" {...g} />
        <path d="M20 8v5M20 16.2v.1" {...g} />
      </>)}
      {art === 'eingang' && (<>
        {/* Ein Ablagekorb: hier liegt etwas und wartet. */}
        <path d="M4 13h4l1.2 2.4h5.6L16 13h4v5H4z" {...g} />
        <path d="M6.5 13 8 5.5h8L17.5 13" {...g} />
      </>)}
      {art === 'partner' && (<>
        {/* Eine Sprechblase: ein Mensch hat etwas geschrieben. */}
        <path d="M4 5.5h16v10H10l-4.5 3.5V15.5H4z" {...g} />
      </>)}
      {art === 'automatik' && (<>
        {/* Eine Uhr: ein Lauf, der zu seiner Zeit nachgesehen hat. */}
        <circle cx="12" cy="12" r="8" {...g} />
        <path d="M12 7.5V12l3 2" {...g} />
      </>)}
      {art === 'wiedervorlage' && (<>
        {/* Ein Kalenderblatt mit einem Merkzeichen: an diesem Tag wollte
            jemand daran erinnert werden. */}
        <path d="M4 6.5h16v13H4z" {...g} />
        <path d="M4 10.5h16M8.5 4v4M15.5 4v4" {...g} />
        <path d="M12 13v3.5" {...g} />
      </>)}
      {art === 'behoerde' && (<>
        {/* Ein Gebäude mit Giebel: die Behörde (.528). */}
        <path d="M4 10h16M5 10v8M9 10v8M15 10v8M19 10v8M3 18h18M3 10l9-5 9 5" {...g} />
      </>)}
      {art === 'freigabe' && (<>
        {/* Ein Umschlag mit einem Haken davor: er geht erst hinaus, wenn
            jemand ihn angesehen hat. */}
        <path d="M3.5 6.5h13v11h-13z" {...g} />
        <path d="M3.5 7l6.5 5 6.5-5" {...g} />
        <path d="M14.5 17.5l2.5 2.5 4-5" {...g} />
      </>)}
      {art === 'rechnung' && (<>
        {/* Ein Blatt mit Zeilen und einem Haken: eine Rechnung, die auf
            die Freigabe wartet. */}
        <path d="M6 3.5h9l3 3v8" {...g} />
        <path d="M6 3.5v17h7" {...g} />
        <path d="M9 9h6M9 12.5h6M9 16h2.5" {...g} />
        <path d="M14.5 18.5l2 2 4-4.5" {...g} />
      </>)}
      {art === 'gutschrift' && (<>
        {/* Ein Blatt mit einem Pfeil hinein: Geld, das zu uns kommt. */}
        <path d="M6 3.5h9l3 3v14H6z" {...g} />
        <path d="M12 8.5v7M9 12.5l3 3 3-3" {...g} />
      </>)}
      {art === 'gebuehr' && (<>
        {/* Ein Geldschein: eine Gebühr, die bezahlt werden will. */}
        <path d="M3 7h18v10H3z" {...g} />
        <circle cx="12" cy="12" r="2.5" {...g} />
        <path d="M6 10v4M18 10v4" {...g} />
      </>)}
      {art === 'zahlentwurf' && (<>
        {/* Ein Geldschein mit einer Uhr: eine Überweisung, die wartet. */}
        <path d="M3 7h13v9H3z" {...g} />
        <circle cx="9.5" cy="11.5" r="2" {...g} />
        <circle cx="18" cy="17" r="3.5" {...g} />
        <path d="M18 15.3V17l1.2 1" {...g} />
      </>)}
      {art === 'mir' && (<>
        {/* Ein Mensch und eine Sprechblase: jemand hat Ihnen etwas
            geschrieben. */}
        <circle cx="9" cy="9" r="3.2" {...g} />
        <path d="M3.5 19c.6-3 2.8-4.5 5.5-4.5 1 0 1.9.2 2.7.6" {...g} />
        <path d="M13 9.5h8v6h-4l-2.6 2.2V15.5H13z" {...g} />
      </>)}
      {art === 'erledigt' && (<>
        <path d="M5 12.5l4.5 4.5L19 7.5" {...g} />
      </>)}
      {art === 'gelesen' && (<>
        {/* Ein Ablagestapel mit einem Haken: von Hand weggelegt. */}
        <path d="M4 8.5h11M4 12h11M4 15.5h7" {...g} />
        <path d="M15 17l2.2 2.2L21 15" {...g} />
      </>)}
    </svg>
  );
}

/**
 * Eine Meldung, in Kopf und Zeilen zerlegt.
 *
 * ORIGINALTEXT MARVIN (25.08.2026)
 *   „außerdem die textliche aufteilung darin ist schlecht"
 *
 * Er hat recht, und es lag daran: die Läufe bauen ihre Meldungen als
 * Aufzählung („Fertigstellungsanzeige fehlt (13)\n• Im Rosengarten 35B ...").
 * Die alte Anzeige warf das als einen Absatz auf den Bildschirm und schnitt
 * nach sechs Zeilen ab - mitten in einer Adresse.
 *
 * Der Text wird deshalb ZERLEGT und nicht formatiert: die Aufzählungspunkte
 * werden Zeilen, und was darüber steht, ist der Vorspann. Mehr als vier
 * Punkte werden zugeklappt, mit der Zahl daneben - „und 9 weitere" ist eine
 * Auskunft, ein abgeschnittener Satz ist keine.
 */
function zerlegen(text: string): { vorspann: string; punkte: string[] } {
  const zeilen = String(text ?? '').split('\n').map((z) => z.trim());
  const punkte: string[] = [];
  const vorspann: string[] = [];
  for (const z of zeilen) {
    if (!z) continue;
    // Die Läufe schreiben „• " davor; manche Stellen einen Spiegelstrich.
    const m = /^[•\-•]\s*(.+)$/.exec(z);
    if (m) punkte.push(m[1]);
    else if (punkte.length === 0) vorspann.push(z);
    // Was NACH der Aufzählung kommt, ist ein Nachsatz („Erzeugen und senden
    // in der Behördenmappe.") und gehört zum Vorspann, nicht in die Liste.
    else vorspann.push(z);
  }
  return { vorspann: vorspann.join(' '), punkte };
}

/**
 * EINE MELDUNG IN IHREN TEILEN, JEDE BAUSTELLE ZUM ANKLICKEN (.229).
 *
 * ORIGINALTEXT MARVIN (25.09.2026)
 *   „das menü der glocke kannst du etwas innovativer umbauen, das ich von da
 *    auch direkt auf den auftrag klicken kann. und etwas länger am pc sein,
 *    die einzelnen nachrichten aufgeräumter vom inhalt."
 *
 * Der Server zerlegt (`aufbauAus` in hausstil.ts) und ordnet jeder Baustelle
 * mit KLS ihren Auftrag zu. Hier wird nur gezeichnet: Abschnittsüberschrift,
 * Baustellenkarten (Anschrift fett, der Rest leise, ein Pfeil rechts), der
 * Hinweis kursiv. Nach 5 Baustellen klappt es zu - mit der Zahl daneben.
 */
function Meldungsaufbau({ teile, oeffne, zeigeBild }: { teile: any[]; oeffne: (id: string) => void; zeigeBild?: (id: string) => void }) {
  const [mehr, setMehr] = useState(false);
  const GRENZE = 5;
  let faelle = 0;
  const gezeigt: any[] = [];
  let versteckt = 0;
  for (const t of teile) {
    const istFall = t.art === 'block' || t.art === 'punkt';
    if (istFall) faelle++;
    if (istFall && !mehr && faelle > GRENZE) { versteckt++; continue; }
    /* Eine Überschrift, unter der nichts mehr gezeigt wird, fällt mit weg. */
    gezeigt.push(t);
  }
  while (gezeigt.length && gezeigt[gezeigt.length - 1].art === 'titel' && versteckt) gezeigt.pop();

  return (
    <div className="meldungsleib aufbau">
      {gezeigt.map((t, i) => {
        if (t.art === 'titel') return <div key={i} className="meldungsabschnitt">{t.text}</div>;
        if (t.art === 'hinweis') return <p key={i} className="meldungshinweis">{t.text}</p>;
        if (t.art === 'text') return <p key={i} className="meldungsvorspann">{t.text}</p>;
        /* DAS BILD DES ROBOTERS (.422), anklickbar in voller Größe. */
        if (t.art === 'bild') {
          const quelle = `/api/gbgs/bild/${t.datei_id}`;
          return (
            <a key={i} href={quelle} target="_blank" rel="noreferrer" className="meldungsbild" title="Bild in voller Größe öffnen"
               onClick={(e) => { if (zeigeBild) { e.preventDefault(); zeigeBild(t.datei_id); } }}>
              <img src={quelle} alt={t.text || 'Bild'} loading="lazy" />
            </a>
          );
        }
        if (t.art === 'punkt') {
          return t.auftrag_id ? (
            <button key={i} type="button" className="baustellenpunkt"
                    onClick={() => oeffne(t.auftrag_id)} title="Auftrag öffnen">
              <span>{t.text}</span><span className="pfeil" aria-hidden="true">›</span>
            </button>
          ) : <div key={i} className="baustellenpunkt still"><span>{t.text}</span></div>;
        }
        /* DIE ANSCHRIFT IST DER KOPF DER KARTE, wo immer sie im Block steht. */
        const alle = [...(t.zeilen as string[])];
        const anschrift = alle.findIndex((z) => z.startsWith('\u{1F4CD}'));
        if (anschrift > 0) alle.unshift(...alle.splice(anschrift, 1));
        const [erste, ...rest] = alle;
        const inhalt = (
          <>
            <span className="karteninhalt">
              <b className="kartenkopf">{erste}</b>
              {rest.map((z, j) => <span key={j} className="kartenzeile">{z}</span>)}
            </span>
            {t.auftrag_id && <span className="pfeil" aria-hidden="true">›</span>}
          </>
        );
        return t.auftrag_id ? (
          <button key={i} type="button" className="baustellenkarte"
                  onClick={() => oeffne(t.auftrag_id)} title="Auftrag öffnen">{inhalt}</button>
        ) : <div key={i} className="baustellenkarte still">{inhalt}</div>;
      })}
      {faelle > GRENZE && (
        <button type="button" className="alslink klein" onClick={() => setMehr(!mehr)}>
          {mehr ? 'weniger zeigen' : `und ${versteckt} weitere zeigen`}
        </button>
      )}
    </div>
  );
}

function Meldungstext({ text }: { text: string }) {
  const [mehr, setMehr] = useState(false);
  const { vorspann, punkte } = zerlegen(text);
  const zeigen = mehr ? punkte : punkte.slice(0, 4);

  return (
    <div className="meldungsleib">
      {vorspann && <p className="meldungsvorspann">{vorspann}</p>}
      {punkte.length > 0 && (
        <ul className="meldungspunkte">
          {zeigen.map((p, i) => <li key={i}>{p}</li>)}
        </ul>
      )}
      {punkte.length > 4 && (
        <button type="button" className="alslink klein"
                onClick={() => setMehr(!mehr)}>
          {mehr ? 'weniger zeigen' : `und ${punkte.length - 4} weitere`}
        </button>
      )}
    </div>
  );
}

/**
 * Nach Tagen gliedern.
 *
 * FÜNFZIG ZEITSTEMPEL UNTEREINANDER SIND KEINE ORDNUNG. „Heute" und „Gestern"
 * beantworten die Frage, die man beim Überfliegen wirklich stellt; ein Datum
 * beantwortet sie erst nach dem Nachrechnen.
 *
 * Gerechnet wird auf dem TAG und nicht auf einer Stundenzahl: „vor 20
 * Stunden" kann gestern oder heute sein, und das ist der ganze Unterschied.
 */
function tagesname(wann: string, heute: string, gestern: string): string {
  const tag = String(wann ?? '').slice(0, 10);
  if (tag === heute) return 'Heute';
  if (tag === gestern) return 'Gestern';
  return datumKurz(tag) || 'Ohne Datum';
}

/** Nur die Uhrzeit. Der Tag steht schon in der Überschrift darüber. */
function uhrzeit(wann: string): string {
  const d = new Date(wann);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('de-DE',
    { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
}

/* DIE ECKEN DES NE4-BEREICHS (.364). Dieselbe Regel wie oben: der Server
   sagt, welche einer bekommt; hier steht nur die Beschriftung. */
const ECKEN_NE4: { wert: string; name: string; kurz: string; farbe: string }[] = [
  { wert: '',               name: 'Alles',                 kurz: 'Alles',     farbe: '' },
  { wert: 'firma',          name: 'Für meine Firma',       kurz: 'Firma',     farbe: 'orange' },
  { wert: 'team',           name: 'Meldungen der Teams',   kurz: 'Teams',     farbe: 'orange' },
  { wert: 'rueckgabe',      name: 'Rückgaben vom Team',    kurz: 'Rückgabe',  farbe: 'rot' },
  { wert: 'nicht_erledigt', name: 'Nicht erledigt',        kurz: 'Offen',     farbe: 'rot' },
  { wert: 'frist',          name: 'Frist in Kürze',        kurz: 'Frist',     farbe: 'orange' },
  { wert: 'wiedervorlage',  name: 'Wiedervorlage fällig',  kurz: 'Vorlage',   farbe: 'orange' },
  { wert: 'gbgs',           name: 'GBGS von Hand',         kurz: 'GBGS',      farbe: 'orange' },
  { wert: 'import',         name: 'Import mit Fehlern',    kurz: 'Import',    farbe: 'orange' },
  { wert: 'automatik',      name: 'Automatik',             kurz: 'Automatik', farbe: 'grau' },
  { wert: 'info',           name: 'Nur zur Info (lief sauber)', kurz: 'Info', farbe: 'grau' },
  { wert: 'gelesen',        name: 'Abgehakt',              kurz: 'Gelesen',   farbe: 'grau' },
  { wert: 'erledigt',       name: 'Erledigt',              kurz: 'Erledigt',  farbe: 'gruen' },
];

/* ARTEN, DIE NICHT ALS ECKE DASTEHEN, haben trotzdem einen Namen (.445):
   „nicht_erledigt" stand roh in der Zeile. */
const ART_NAMEN: Record<string, string> = {
  nicht_erledigt: 'Nicht erledigt', versand: 'Nicht zugestellt', freigabe: 'Wartet auf Freigabe', behoerde: 'Behörde',
  rechnung: 'Rechnung zur Prüfung', gutschrift: 'Neue Gutschrift', gebuehr: 'Gebühr', zahlentwurf: 'In Revolut freigeben',
  wiedervorlage: 'Wiedervorlage', eingang: 'Posteingang', partner: 'Partnerfirma', mir: 'Für mich',
  rueckgabe: 'Rückgabe vom Team', frist: 'Frist in Kürze', gbgs: 'GBGS von Hand', import: 'Import',
  firma: 'Für meine Firma', team: 'Meldung vom Team',
};

export function Glocke(
  { ich, oeffne, gehZu, bereich = 'ne3', wo }: { ich: Anmelder; oeffne: (id: string, reiter?: string) => void;
    gehZu?: (seite: string) => void; bereich?: string; wo?: string }
) {
  const [offen, setOffen] = useState(false);
  /* DAS BILD DES ROBOTERS IM DATEIFENSTER, mit Zoom (.447). */
  const [bild, setBild] = useState<string | null>(null);
  const [zahl, setZahl] = useState(0);
  const [dringend, setDringend] = useState(0);
  const [ecke, setEcke] = useState('');
  const ECKEN_HIER = bereich === 'ne4' ? ECKEN_NE4 : ECKEN;
  const [d, setD] = useState<any>(null);
  const [laeuft, setLaeuft] = useState(false);
  const feld = useRef<HTMLDivElement>(null);

  /* DIE GLOCKE HAT JEDER.
   *
   * ORIGINALTEXT MARVIN (02.09.2026)
   *   „diese glocke fehlt zb bei Nicole. bitte sicherstellen, dass die bei
   *    allen benutzern ist, damit man dort auch signalisieren kann, mit dem
   *    hinblick später auf die app."
   *
   * Hier stand `darf(ich, 'meldung.lesen') && !ich.istSub`, und beide Hälften
   * waren falsch am Platz. Die Glocke ist kein Bildschirm, sondern ein Teil
   * des Rahmens; WAS darin steht, entscheidet der Server über die
   * Sichtbarkeitsgrenze (`routen/meldungen.ts`). Eine Anzeige, die etwas
   * verbirgt, hat es ohnehin im Speicher - der Riegel gehört in die Abfrage
   * und nicht hierher. */
  const darfSehen = !!ich.benutzerId;

  // Die Zahl wird nachgesehen, nicht geschoben. Alle drei Minuten reicht: die
  // Automatik läuft im Stundentakt, und eine Glocke, die im Sekundentakt
  // fragt, ist eine Abfrage im Sekundentakt.
  useEffect(() => {
    if (!darfSehen) return;
    let weg = false;
    const hol = () => api.meldungZahl()
      .then((z) => { if (!weg) { setZahl(z.offen); setDringend(z.dringend); } })
      .catch(() => {});
    hol();
    const t = setInterval(hol, 180_000);
    return () => { weg = true; clearInterval(t); };
  }, [darfSehen, bereich, ich.benutzerId]);

  // Beim Aufklappen die Liste holen, und beim Wechsel der Ecke neu. Vorher
  // nicht: hundertfünfzig Zeilen zu laden, die niemand ansieht, ist
  // hundertfünfzig Zeilen zu viel.
  useEffect(() => {
    if (!offen) return;
    setLaeuft(true);
    api.meldungen(ecke).then(setD).catch(() => {}).finally(() => setLaeuft(false));
  }, [offen, ecke, bereich]);
  /* BEIM BEREICHSWECHSEL VON VORN: die alte Liste und Ecke gehören dem anderen Bereich. */
  useEffect(() => { setD(null); setEcke(''); }, [bereich]); // eslint-disable-line react-hooks/exhaustive-deps
  /* SEITENWECHSEL UND ZUGANGSWECHSEL SCHLIESSEN DIE LISTE (.372): wer über
     das Menü weitergeht oder sich in einen anderen Zugang versetzt, will die
     neue Seite sehen, nicht die Liste von vorhin davor. Die Zahl wird mit dem
     Zugang neu geholt. */
  useEffect(() => { setOffen(false); }, [wo, ich.benutzerId]);
  useEffect(() => { setD(null); setEcke(''); }, [ich.benutzerId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Aussen klicken und Escape schliessen. Dieselbe Regel wie beim Klappmenü.
  useEffect(() => {
    if (!offen) return;
    const aus = (e: MouseEvent) => {
      if (feld.current && !feld.current.contains(e.target as Node)) setOffen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOffen(false); };
    document.addEventListener('mousedown', aus);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', aus);
      document.removeEventListener('keydown', esc);
    };
  }, [offen]);

  if (!darfSehen) return null;

  async function frisch() {
    const [z, neu] = await Promise.all([
      api.meldungZahl().catch(() => ({ offen: zahl, dringend })),
      api.meldungen(ecke).catch(() => d),
    ]);
    setZahl(z.offen); setDringend(z.dringend); setD(neu);
  }

  /**
   * Abhaken - und die Zeile geht.
   *
   * SIE WIRD HIER AUS DER LISTE GENOMMEN UND NICHT NEU GELADEN. Ein zweiter
   * Abruf würde die ganze Liste umbauen, während der Daumen noch über dem
   * nächsten Haken steht; die Zeile darunter rutscht dann unter den Finger.
   * Genommen wird sie nur, wenn sie in dieser Ecke nichts mehr zu suchen hat -
   * in der Ecke „Gelesen" bewirkt der Haken das Gegenteil, und dann ist es die
   * Rücknahme, die verschwindet.
   */
  async function abhaken(m: any) {
    const jetztGelesen = !m.gelesen;
    await api.meldungGelesen(m.id, m.art, jetztGelesen).catch(() => {});
    setD((alt: any) => alt && {
      ...alt,
      zeilen: alt.zeilen.filter(
        (x: any) => !(x.id === m.id && x.art === m.art)),
    });
    const z = await api.meldungZahl().catch(() => ({ offen: zahl, dringend }));
    setZahl(z.offen); setDringend(z.dringend);
    /* NUR DIE ZAHLEN AN DEN ECKEN, NICHT DIE ZEILEN. Sonst springt die Liste
       unter dem Daumen, während man drei Meldungen hintereinander abhakt. */
    const frischeZahlen = await api.meldungen(ecke).catch(() => null);
    if (frischeZahlen) {
      setD((alt: any) => (alt ? { ...alt, jeArt: frischeZahlen.jeArt } : alt));
    }
  }

  async function alle() {
    // NUR, WAS JETZT DASTEHT. Sonst verschluckt der Knopf die Meldung, die in
    // derselben Sekunde hereinkommt.
    /* DER STAND KOMMT VOM SERVER (.229). Hier stand die Zeit der OBERSTEN
       Zeile - oben stehen aber die dringenden, und alles Jüngere blieb
       ungehakt stehen. */
    const bis = d?.stand ?? undefined;
    await api.meldungAlleGelesen(bis, ecke || undefined).catch(() => {});
    await frisch();
  }

  const zeilen: any[] = d?.zeilen ?? [];
  const jeArt: any[] = d?.jeArt ?? [];
  const offenJe = (a: string) =>
    a ? (jeArt.find((x) => x.art === a)?.offen ?? 0) : zahl;
  const alleJe = (a: string) => jeArt.find((x) => x.art === a)?.n ?? 0;

  /* HEUTE UND GESTERN ALS TAG, nicht als Stundenabstand. Beide in Ortszeit -
     `toISOString()` rechnet nach UTC um, und an einem Sommerabend um 23 Uhr
     stünde dort schon der nächste Tag. */
  const jetzt = new Date();
  /* UEBER `tagIso` AUS `api.ts`. Hier stand `getTimezoneOffset()`, und das
     ist der Versatz des BETRACHTERS und nicht der von Berlin: wer aus einer
     anderen Zone hineinsieht, bekam ein „Heute", das die Anzeige daneben
     anders nennt. */
  const heute = tagIso(jetzt);
  const gestern = tagIso(new Date(jetzt.getTime() - 86400000));

  /* Die Reihenfolge kommt vom Server (dringend zuerst, dann neu zuerst) und
     wird hier NICHT umsortiert - nur gruppiert. Wer sie hier noch einmal
     sortierte, hätte zwei Reihenfolgen für dieselbe Liste. */
  const gruppen: { tag: string; zeilen: any[] }[] = [];
  for (const m of zeilen) {
    const t = tagesname(m.gesendet_am, heute, gestern);
    const letzte = gruppen[gruppen.length - 1];
    if (letzte && letzte.tag === t) letzte.zeilen.push(m);
    else gruppen.push({ tag: t, zeilen: [m] });
  }

  /* WOHIN EIN KLICK AUF EINE MELDUNG FÜHRT. Eine Freigabe (oder die Info
     „FAZ zum Versand bereit") öffnet die Behördenmappe mit dem Prüffenster;
     alles andere die Übersicht des Auftrags. */
  const zumZiel = (m: any, auftragId: string) => {
    /* NE4 (.364): der Auftrag gehört zu ne4.auftrag und wird in der
       NE4-Karte geöffnet, nicht in der NE3-Akte. */
    if (bereich === 'ne4') {
      try { sessionStorage.setItem('mds.ne4.oeffne', auftragId); } catch { /* ohne Speicher nur die Seite */ }
      setOffen(false);
      /* .592: eine Firma öffnet das Haus in „Meine Aufträge". */
      gehZu?.(m.art === 'firma' ? 'ne4_firmenauftraege' : 'ne4_karte');
      return;
    }
    if (istFreigabe(m) && auftragId === m.auftrag_id) {
      freigabeVormerken(auftragId, m.art === 'freigabe' ? m.id : null);
      oeffne(auftragId, 'behoerde');
    } else oeffne(auftragId);
  };

  return (
    <div className="glocke" ref={feld}>
      <button type="button"
              className={'glockenknopf' + (dringend ? ' dringend' : '')}
              onClick={() => setOffen(!offen)} aria-expanded={offen}
              title={zahl ? `${zahl} ungelesen` : 'Nichts Neues'}>
        {/* Eine Glocke, gezeichnet statt geladen: ein Symbolzeichen sieht auf
            jedem zweiten Gerät anders aus. */}
        <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true">
          <path d="M12 3a5.5 5.5 0 0 0-5.5 5.5v3.2l-1.4 2.6a.8.8 0 0 0 .7 1.2h12.4a.8.8
                   0 0 0 .7-1.2l-1.4-2.6V8.5A5.5 5.5 0 0 0 12 3Z"
                fill="none" stroke="currentColor" strokeWidth="1.7"
                strokeLinejoin="round" />
          <path d="M10 18.5a2 2 0 0 0 4 0" fill="none" stroke="currentColor"
                strokeWidth="1.7" strokeLinecap="round" />
        </svg>
        {/* DIE ZAHL MUSS ZU LESEN SEIN.
            Marvin, 25.08.2026: „oben die glocke mit der benarichtigung sind
            die zahlen drin nicht zu erkennen."
            Sie stand auf 10,5 Pixel in einem 17-Pixel-Kreis, der halb über
            dem Rand des Knopfes hing. Jetzt: grösser, mit eigener Fläche und
            einem Ring in der Farbe des Untergrunds, damit die Ziffern nicht
            am Glockenrand kleben. */}
        {/* DIE ZIFFER STEHT IN EINEM EIGENEN KASTEN, und das ist kein
            Beiwerk: nur ein BLOCK lässt sich auf seine Ziffernhöhe
            beschneiden. Warum das nötig ist, steht bei `.glockenziffer` in
            `stil.css` - kurz: ohne den Schnitt hängt die Mitte an der
            Schriftart, und die ist auf jedem Rechner eine andere. */}
        {zahl > 0 && (
          <span className="glockenzahl">
            <span className="glockenziffer">{zahl > 99 ? '99+' : zahl}</span>
          </span>
        )}
      </button>

      {offen && (
        <div className="glockenliste">
          <div className="glockenkopf">
            <b>Benachrichtigungen</b>
            <div className="wachsen" />
            {zahl > 0 && !RUHIG.has(ecke) && (
              <button type="button" className="knopf klein" onClick={alle}>
                {ecke ? 'Diese abhaken' : 'Alle abhaken'}
              </button>
            )}
          </div>

          {/* NICHT NUR DIE AUTOMATIK, und der Filter sagt, was es sonst gibt.
              Marvin, 25.08.2026: „wir braucehn ja nicht nur die infos der
              automatik." */}
          <div className="glockenecken">
            {ECKEN_HIER.filter((e) => QUER.has(e.wert)
                              || (d?.ecken ?? []).includes(e.wert)).map((e) => {
              /* IN DEN RUHIGEN ECKEN ZÄHLT NICHT DAS UNGELESENE. Dort gibt es
                 nichts abzuhaken; die Zahl ist, wie viel weggefallen ist. */
              const n = RUHIG.has(e.wert) ? alleJe(e.wert) : offenJe(e.wert);
              return (
                <button key={e.wert} type="button"
                        className={'ecke' + (ecke === e.wert ? ' gewaehlt' : '')
                                 + (e.farbe ? ' ' + e.farbe : '')}
                        title={e.name}
                        onClick={() => setEcke(e.wert)}>
                  {e.wert && <Eckenzeichen art={e.wert} />}
                  <span>{e.kurz}</span>
                  {n > 0 && <span className="eckenzahl">{n}</span>}
                </button>
              );
            })}
          </div>

          {/* WAS DIESE ECKE IST, STEHT IN EINEM SATZ. Ohne ihn liest sich
              „Erledigt" wie „abgehakt" und nicht wie „hat sich von selbst
              erledigt", und das ist ein Unterschied. */}
          {ecke === 'erledigt' && (
            <p className="klein leise glockenerklaerung">
              Was sich von selbst erledigt hat. Eine Meldung der Automatik gilt
              bis zum nächsten Lauf desselben Anlasses; eine nicht zugestellte
              Mail ist erledigt, sobald dieselbe Post doch ankam. Es steht hier
              und nicht in der Liste - eine Meldung, die still weggeht, ist von
              einer verlorenen nicht zu unterscheiden.
            </p>
          )}
          {ecke === 'gelesen' && (
            <p className="klein leise glockenerklaerung">
              Was abgehakt ist. Ein Klick auf den Haken holt eine Zeile zurück
              nach oben. Ein Haken an einer Meldung gilt für alle, die sie
              bekommen haben, mit dem Namen dessen, der ihn gesetzt hat;
              „Alle abhaken" gilt nur für Sie.
            </p>
          )}

          {ecke === 'wiedervorlage' && (
            <p className="klein leise glockenerklaerung">
              Was heute oder früher fällig ist und noch niemand erledigt hat.
              Diese Ecke fragt einen Zustand: eine Wiedervorlage steht hier,
              bis sie in der Auftragsakte erledigt wird - sie fällt nicht
              heraus, weil der nächtliche Lauf noch einmal durch war.
            </p>
          )}
          {ecke === 'freigabe' && (
            <p className="klein leise glockenerklaerung">
              Behördensendungen, die auf einen zweiten Menschen warten. Der
              Schalter dazu heißt „Behördenpost erst nach Freigabe" und ist
              eine Schulungsstufe: so geht kein unscharfes Foto an eine
              Gemeinde. Auch diese Ecke fragt einen Zustand - sobald
              freigegeben oder abgelehnt ist, fällt die Zeile heraus.
            </p>
          )}
          {ecke === 'mir' && (
            <p className="klein leise glockenerklaerung">
              Was der Innendienst zu Ihren Aufträgen geschrieben hat und für
              Sie sichtbar gemacht hat: die Begründung einer Freigabe, eine
              Rückfrage, eine Anweisung zur Baustelle. Was Ihre eigene Firma
              geschrieben hat, steht hier nicht.
            </p>
          )}

          {laeuft && !zeilen.length && (
            <p className="klein leise glockenleer">Wird geladen.</p>
          )}
          {!laeuft && zeilen.length === 0 && (
            <p className="klein leise glockenleer">
              {/* EINE LEERE LISTE OHNE GRUND LIEST SICH WIE EIN FEHLER. Wer
                  weder eine Firma noch das Recht auf die Meldungen hat, hat
                  eine Glocke und nichts darin - und der Satz dazu kommt vom
                  Server, damit hier keine zweite Erklärung entsteht. */}
              {d?.hinweis
                ? d.hinweis
                : ecke === 'erledigt'
                ? 'Es hat sich noch nichts von selbst erledigt.'
                : ecke === 'gelesen'
                ? 'Sie haben noch nichts abgehakt.'
                : ecke ? 'In dieser Ecke steht nichts.'
                : 'Nichts Neues.'}
            </p>
          )}

          {gruppen.map((g) => (
            <div key={g.tag} className="glockentag">
              <h4>{g.tag}</h4>
              {g.zeilen.map((m) => {
              /* IN DER ECKE „GELESEN" STEHT DIE MELDUNG GANZ DA. Genau dorthin
                 geht man, um etwas WIEDERZUFINDEN - eine Liste aus lauter
                 Überschriften hilft dabei nicht. In jeder anderen Ecke kommt
                 Gelesenes gar nicht mehr vor. */
              const ganz = !m.gelesen || ecke === 'gelesen';
              return (
                <div key={m.art + m.id}
                     className={'meldung ' + (ECKEN_HIER.find((e) => e.wert === m.art)
                                              ?.farbe ?? 'grau')
                              + (m.gelesen ? ' gelesen' : '')
                              + (m.dringend && !m.erledigt ? ' dringend' : '')
                              + (m.erledigt ? ' erledigt' : '')}>
                  <div className="meldungskopf">
                    <span className="meldungszeichen" aria-hidden="true">
                      <Eckenzeichen art={m.erledigt ? 'erledigt' : m.art} />
                    </span>
                    <div className="wachsen">
                      <b className="meldungstitel">{m.titel}</b>
                      <div className="meldungszeile klein leise">
                        {m.art === 'automatik'
                          ? anlassText(m.anlass, d?.anlaesse)
                          : ECKEN_HIER.find((e) => e.wert === m.art)?.name ?? ART_NAMEN[m.art] ?? anlassText(m.art, d?.anlaesse)}
                        {', '}
                        {/* NUR DIE UHRZEIT: der Tag steht in der Überschrift
                            darüber. Und europäisch, hier wie überall.
                            Marvin, 25.08.2026: „da steht das datum nicht
                            europäisch drin. bitte ändern merke dir das für
                            immer." */}
                        {uhrzeit(m.gesendet_am)}
                        {m.abgehakt_von && <span className="leise">, abgehakt von {m.abgehakt_von}</span>}
                        {m.nur_hier && (
                          <span className="merker warnung"
                                title="Testmodus oder kein Automatikchat: diese
                                       Meldung ist nie versendet worden.">
                            nur hier
                          </span>
                        )}
                      </div>
                    </div>
                    {/* EIN HAKEN IST EIN HAKEN UND KEIN SATZ.
                        Hier stand fünfzigmal derselbe Knopf mit der Aufschrift
                        „Gelesen"; das ist die Sorte Wiederholung, die eine
                        Liste monoton macht. Was er tut, steht im Titel - und
                        er behält seine 30 Pixel, damit ein Daumen ihn trifft. */}
                    <button type="button"
                            className={'meldungshaken' + (m.gelesen ? ' an' : '')}
                            onClick={() => abhaken(m)}
                            aria-pressed={!!m.gelesen}
                            title={m.gelesen
                              ? `Wieder öffnen, für alle${m.abgehakt_von ? ` (abgehakt von ${m.abgehakt_von})` : ''}`
                              : 'Abhaken, für alle, die die Meldung bekommen haben'}>
                      <svg viewBox="0 0 24 24" width="14" height="14"
                           aria-hidden="true">
                        <path d="M5 12.5l4.5 4.5L19 7.5" fill="none"
                              stroke="currentColor" strokeWidth="2.4"
                              strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>

                  {/* GELESENES SCHRUMPFT AUF SEINE ÜBERSCHRIFT. Es ist nicht
                      weg - man findet es wieder -, aber es nimmt keinen Platz
                      mehr weg. Ohne das steht eine abgearbeitete Liste genauso
                      hoch da wie eine unbearbeitete, und dann sieht Arbeit aus
                      wie keine Arbeit. */}
                  {ganz && (Array.isArray(m.teile) && m.teile.length
                    ? <Meldungsaufbau teile={m.teile}
                                      oeffne={(id) => { setOffen(false); zumZiel(m, id); }}
                                      zeigeBild={(id) => setBild(id)} />
                    : <Meldungstext text={m.text ?? ''} />)}

                  {ganz && m.erledigt_grund && (
                    <p className="meldungserledigt klein">{m.erledigt_grund}</p>
                  )}

                  {/* EINE MELDUNG, DIE EIN PROBLEM NENNT UND NICHT SAGT, WO ES
                      STEHT, IST EINE HALBE MELDUNG. */}
                  {/* FREIGABE UND KORREKTUR FÜHREN AUF DIE SEITE „FREIGABEN" und
                      nicht in die Akte: dort steht, was zu tun ist, mit den
                      Positionen der Korrektur (23.09.2026). */}
                  {ganz && gehZu && (m.anlass === 'rechnung_pruefen' || m.anlass === 'gutschrift_neu' || m.anlass === 'gebuehr_faellig') && (
                    <button className="meldungsziel klein"
                            onClick={() => { setOffen(false);
                              if (m.anlass === 'rechnung_pruefen') { try { localStorage.setItem('abrechnung.schritt', 'schritt2'); } catch { /* egal */ } }
                              gehZu(m.anlass === 'rechnung_pruefen' ? 'rechnungen' : m.anlass === 'gebuehr_faellig' ? 'gebuehren' : 'leb_abrechnung'); }}>
                      <span>{m.anlass === 'rechnung_pruefen' ? 'Zur Freigabe der Rechnungen'
                             : m.anlass === 'gebuehr_faellig' ? 'Zu den Gebühren' : 'Zur LEB-Abrechnung'}</span>
                    </button>
                  )}
                  {ganz && gehZu && m.anlass === 'revolut_freigabe' && (
                    <button className="meldungsziel klein"
                            onClick={() => { setOffen(false); gehZu('rechnungen_bezahlen'); }}>
                      <span>Zu „Rechnungen bezahlen“</span>
                    </button>
                  )}
                  {ganz && gehZu && String(m.anlass ?? '').startsWith('firmen_') && (
                    <button className="meldungsziel klein"
                            onClick={() => { setOffen(false); gehZu('freigaben'); }}>
                      <span>Zu den Freigaben</span>
                    </button>
                  )}
                  {/* EIN EINBLASSTOP, DER NICHT BEI DER TELEKOM IST (.328).
                      ORIGINALTEXT MARVIN (07.10.2026): „ja bitte auch in die
                      glocke bringen als info, aber auch "abhaken" ermöglichen,
                      falls es manuell versendet wurde." Der Weg zur Meldung
                      führt in die Akte (Kasten oben, Knopf „Jetzt an die
                      Telekom melden"); „Schon gemeldet" hakt ab. */}
                  {ganz && m.auftrag_id && m.art === 'nicht_erledigt' && /^Einblasstop: noch nicht an die Telekom/.test(String(m.titel ?? '')) && (
                    <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button type="button" className="knopf haupt klein meldungspruefen"
                              onClick={() => { setOffen(false); zumZiel(m, m.auftrag_id); }}>
                        An die Telekom melden
                      </button>
                      <button type="button" className="knopf klein"
                              title={'Der Stop wurde außerhalb der Plattform an die Telekom gemeldet: Datum eintragen, die Zeile wird zu „Nicht erledigt".'}
                              onClick={async () => {
                                try { await api.einblasstopSchonGemeldet(m.auftrag_id); await frisch(); }
                                catch { /* die Zeile bleibt, der Server sagt beim nächsten Laden, was ist */ }
                              }}>
                        Schon gemeldet (außerhalb)
                      </button>
                    </div>
                  )}
                  {/* EINE FREIGABE FÜHRT INS PRÜFFENSTER (.233): FAZ, Fotos,
                      Freigeben oder Ablehnen mit Kommentar an den Partner. */}
                  {ganz && m.auftrag_id && istFreigabe(m) && !m.erledigt && (
                    <button type="button" className="knopf haupt klein meldungspruefen"
                            onClick={() => { setOffen(false); zumZiel(m, m.auftrag_id); }}>
                      Ansehen und freigeben
                    </button>
                  )}
                  {ganz && m.auftrag_id && !(gehZu && String(m.anlass ?? '').startsWith('firmen_')) && m.anlass !== 'rechnung_pruefen'
                    && !(m.teile ?? []).some((t: any) => t.auftrag_id === m.auftrag_id) && (
                    <button className="meldungsziel klein"
                            onClick={() => { setOffen(false); zumZiel(m, m.auftrag_id); }}>
                      <svg viewBox="0 0 24 24" width="13" height="13"
                           aria-hidden="true">
                        <path d="M12 21s6.5-6.1 6.5-10.5a6.5 6.5 0 1 0-13 0C5.5 14.9 12 21 12 21Z"
                              fill="none" stroke="currentColor" strokeWidth="1.7"
                              strokeLinejoin="round" />
                        <circle cx="12" cy="10.5" r="2.2" fill="none"
                                stroke="currentColor" strokeWidth="1.7" />
                      </svg>
                      <span>
                        {m.strasse} {m.hausnummer}
                        {m.ort_text ? `, ${m.plz ?? ''} ${m.ort_text}` : ''}
                      </span>
                    </button>
                  )}
                </div>
              );
              })}
            </div>
          ))}
        </div>
      )}
      {bild && (
        <Dateifenster datei={{ id: bild, dateiname: 'Bild aus dem GBGS.png', mime: 'image/png', adresse: `/api/gbgs/bild/${bild}` }}
                      zu={() => setBild(null)} />
      )}
    </div>
  );
}
